"use client"
import { ArrowLeft, CalendarDays, Leaf, Pencil, Tag } from "lucide-react";
import React from "react";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Separator } from "../../components/ui/separator";
import { Skeleton } from "../../components/ui/skeleton";
import { StatusBadge } from "../../components/ui/data-table/status-badge";
import { useEspece } from "../../hooks/api/especes";
import { formatDate } from "../../lib/utils";
import { imageService } from "../../lib/services";

interface EspeceDetailProps {
  especeId: number;
  onBack: () => void;
  onEdit: () => void;
}

// ─── Reusable field ────────────────────────────────────────────────────────────
function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm font-medium text-foreground">{value ?? "—"}</dd>
    </div>
  );
}

// ─── Section heading ───────────────────────────────────────────────────────────
function SectionHeading({
  icon: Icon,
  children,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
      {Icon && <Icon className="h-3.5 w-3.5 text-primary shrink-0" />}
      {children}
    </h3>
  );
}

// ─── Loading skeleton ──────────────────────────────────────────────────────────
function DetailSkeleton() {
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4 pb-1">
        <div className="space-y-2">
          <Skeleton className="h-6 w-52" />
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
        <div className="flex gap-2 shrink-0">
          <Skeleton className="h-9 w-24 rounded-md" />
          <Skeleton className="h-9 w-24 rounded-md" />
        </div>
      </div>
      <Separator />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ))}
      </div>
      <Separator />
      <div className="grid grid-cols-2 gap-x-6 gap-y-5">
        <div className="space-y-1.5">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export function EspeceDetail({ especeId, onBack, onEdit }: EspeceDetailProps) {
  const { data: espece, isLoading, error } = useEspece(especeId);

  if (isLoading) return <DetailSkeleton />;

  if (error || !espece?.data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {error?.message ||
            "Impossible de charger les détails de l'espèce. Veuillez réessayer."}
        </AlertDescription>
      </Alert>
    );
  }

  const data = espece.data;

  const formatTarif = (tarif: number | null | undefined): string => {
    if (tarif == null) return "—";
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    }).format(tarif);
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 pb-5">
        {/* Name + meta */}
        <div className="space-y-1.5 min-w-0">
          <h2 className="text-xl font-semibold tracking-tight text-foreground">
            {data.genre ? `${data.genre} ` : ""}
            <span className="italic">{data.name?.toLowerCase()}</span>
          </h2>
          {data.nom_fr && (
            <p className="text-sm text-muted-foreground">{data.nom_fr}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            <StatusBadge
              tone={data.is_deleted ? "deleted" : "active"}
              label={data.is_deleted ? "Supprimée" : "Active"}
              withIcon
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Retour
          </Button>
          {!data.is_deleted && (
            <Button onClick={onEdit}>
              <Pencil className="mr-1.5 h-4 w-4" />
              Modifier
            </Button>
          )}
        </div>
      </div>

      <Separator />

      <div className="py-6 space-y-6">
        {/* ── Identification ── */}
        <div className="space-y-4">
          <SectionHeading icon={Leaf}>Identification</SectionHeading>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
            <Field label="Identifiant" value={`#${data.id}`} />
            <Field label="Genre" value={data.genre || "—"} />
            <Field
              label="Espèce"
              value={
                data.name ? (
                  <span className="italic">{data.name.toLowerCase()}</span>
                ) : (
                  "—"
                )
              }
            />
            <Field label="Cultivar" value={
              data.cultivar
                ? data.cultivar.charAt(0).toUpperCase() + data.cultivar.slice(1).toLowerCase()
                : "—"
            } />
            <Field label="Nom français" value={data.nom_fr || "—"} />
          </dl>
        </div>

        <Separator />

        {/* ── Classification & tarif ── */}
        <div className="space-y-4">
          <SectionHeading icon={Tag}>Classification & tarification</SectionHeading>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
            <Field label="Catégorie" value={data.categorie || "—"} />
            <Field label="Tarif" value={formatTarif(data.tarif)} />
            {data.indice_espece != null && (
              <Field label="Indice espèce" value={data.indice_espece} />
            )}
          </dl>
        </div>

        <Separator />

        {/* ── Dates ── */}
        <div className="space-y-4">
          <SectionHeading icon={CalendarDays}>Dates</SectionHeading>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
            <Field
              label="Créé le"
              value={
                data.created_at
                  ? formatDate(new Date(data.created_at))
                  : "—"
              }
            />
          </dl>
        </div>

        {/* ── Image ── */}
        {data.img_url && (
          <>
            <Separator />
            <div className="space-y-3">
              <SectionHeading>Illustration</SectionHeading>
              <div className="rounded-lg overflow-hidden border border-border bg-muted/30">
                <img
                  src={imageService.getOptimizedImageUrl(data.img_url, "espece", 800, 600)}
                  alt={data.name || "Espèce"}
                  className="w-full h-auto max-h-80 object-contain p-4"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
